/**
 * Fahryo Centralized Logger
 * 
 * - Dev: logs everything to console
 * - Production: only warn + error, auto-reports errors to Sentry
 * - Consistent format: [Module] message
 * - Feature-area tagging for Sentry issue triage
 */
import * as Sentry from '@sentry/react';


const IS_PROD = typeof window !== 'undefined' && window.location.hostname !== 'localhost';

function formatArgs(module: string, msg: string, ...args: unknown[]): unknown[] {
  return [`[${module}] ${msg}`, ...args];
}

const logger = {
  /** Debug — only shows in dev */
  debug(module: string, msg: string, ...args: unknown[]) {
    if (!IS_PROD) {
      console.debug(...formatArgs(module, msg, ...args));
    }
  },

  /** Info — only shows in dev */
  info(module: string, msg: string, ...args: unknown[]) {
    if (!IS_PROD) {
      console.info(...formatArgs(module, msg, ...args));
    }
  },

  /** Warning — always shows, adds Sentry breadcrumb in production */
  warn(module: string, msg: string, ...args: unknown[]) {
    console.warn(...formatArgs(module, msg, ...args));
    if (IS_PROD) {
      Sentry.addBreadcrumb({
        category: module,
        message: msg,
        level: 'warning',
      });
    }
  },

  /** Error — always shows + reports to Sentry in production */
  error(module: string, msg: string, error?: unknown, extra?: Record<string, unknown>) {
    console.error(...formatArgs(module, msg, error));
    if (IS_PROD && error instanceof Error) {
      Sentry.captureException(error, {
        tags: { module, feature_area: module.toLowerCase() },
        extra: { message: msg, ...extra },
      });
    } else if (IS_PROD) {
      Sentry.captureMessage(`[${module}] ${msg}`, {
        level: 'error',
        tags: { module, feature_area: module.toLowerCase() },
        extra: { error, ...extra },
      });
    }
  },

  /** Capture a business event in Sentry (non-error) */
  event(module: string, msg: string, data?: Record<string, unknown>) {
    if (IS_PROD) {
      Sentry.captureMessage(`[${module}] ${msg}`, {
        level: 'info',
        tags: { module, feature_area: module.toLowerCase() },
        extra: data,
      });
    }
  },
};

export default logger;
